import { useCallback, useEffect, useRef } from 'react';

import { consoleInfoLog, getStorageValue, setStorageValue } from 'App/Helpers';

import { getOptedInDeviceId } from '../getOptedInDeviceId';
import { initOneSignal } from '../initOneSignal';
import { useOneSignalConsent } from '../useOneSignalConsent';

const updateStorageWithPromptCount = (storageKey) => {
    const browserPushPromptCount = Number(
        getStorageValue(storageKey, 'push.brwPromptCount')
    );
    const updatedCount = Number.isFinite(browserPushPromptCount)
        ? browserPushPromptCount + 1
        : 1;

    setStorageValue(storageKey, 'push.brwPromptCount', updatedCount);
};

/**
 * Bootstraps OneSignal and writes readiness via `setPushServiceReady`.
 *
 * By default the SDK initializes as soon as functional consent is granted.
 * Pass `deferInit` for LPs that postpone it until an explicit user action
 * (desktop Play) and call `init` there.
 *
 * @param {Object} params
 * @param {string} params.oneSignalAppId - OneSignal application id for the active locale.
 * @param {string} params.storageKey - Base storage namespace for push values.
 * @param {(id: string) => void} params.setOnesignalId - Publishes the OneSignal player id.
 * @param {string} params.pushStatus - Current notification permission status.
 * @param {(status: string) => void} params.updatePushStatus - Persists permission status.
 * @param {boolean} params.pushServiceReady - Whether the push SDK has finished init.
 * @param {(ready: boolean) => void} params.setPushServiceReady - Marks the SDK ready.
 * @param {boolean | undefined} params.functionalConsent - Functional cookie consent.
 * @param {boolean} [params.deferInit=false] - When true, init happens only via `init`.
 * @param {Function} params.sendGameDesignEvent - Deferred GameAnalytics design event.
 * @param {Function} params.sendGoogleBasicEvent - Deferred Google Analytics basic event.
 * @returns {{
 *   init: () => void,
 * }}
 */
export const useOneSignalInit = ({
    sendGameDesignEvent,
    sendGoogleBasicEvent,
    storageKey,
    pushStatus,
    oneSignalAppId,
    setOnesignalId,
    updatePushStatus,
    pushServiceReady,
    setPushServiceReady,
    functionalConsent,
    deferInit = false,
}) => {
    const initCalledRef = useRef(false);
    const sendGameDesignEventRef = useRef(sendGameDesignEvent);
    const sendGoogleBasicEventRef = useRef(sendGoogleBasicEvent);

    sendGameDesignEventRef.current = sendGameDesignEvent;
    sendGoogleBasicEventRef.current = sendGoogleBasicEvent;

    const handlePermissionPromptDisplay = useCallback(() => {
        sendGameDesignEventRef.current('Push:NPopupShow');
        sendGoogleBasicEventRef.current('push', 'native_popup_show');
        updateStorageWithPromptCount(storageKey);
    }, [storageKey]);

    const handlePermissionChange = useCallback(
        (permission) => {
            const status = permission ? 'granted' : Notification.permission;

            updatePushStatus(status);
            consoleInfoLog(
                `## OneSignal: Notification Permission Changed to ${status}`,
                'push'
            );
        },
        [updatePushStatus]
    );

    const handlePushSubscriptionChange = useCallback(
        (event) => {
            const deviceId = getOptedInDeviceId(event);

            if (!deviceId) return;

            setOnesignalId(deviceId);
            consoleInfoLog(`## OneSignal: Device Id - ${deviceId}`, 'push');
        },
        [setOnesignalId]
    );

    const init = useCallback(() => {
        if (pushStatus === 'notAvailable' || !functionalConsent) return;
        if (!oneSignalAppId || initCalledRef.current) return;

        initCalledRef.current = true;

        initOneSignal({
            oneSignalAppId,
            setPushServiceReady,
            requiresUserPrivacyConsent: true,
            onPermissionPromptDisplay: handlePermissionPromptDisplay,
            onPushSubscriptionChange: handlePushSubscriptionChange,
            onPermissionChange: handlePermissionChange,
        }).then((didInit) => {
            if (!didInit) initCalledRef.current = false;
        });
    }, [
        pushStatus,
        oneSignalAppId,
        setPushServiceReady,
        functionalConsent,
        handlePermissionPromptDisplay,
        handlePermissionChange,
        handlePushSubscriptionChange,
    ]);

    useEffect(() => {
        if (deferInit) return;

        init();
    }, [deferInit, init]);

    useOneSignalConsent({ pushServiceReady, functionalConsent });

    return { init };
};
